// Each kind of item a transcript holds, as the page shows it: the prompts
// people sent, the agent's Markdown as it streams, thinking folded, a tool
// call as one row that opens (an edit as its diff, a sub-agent's calls under
// it), the turn's plan, a notice, and the line that ends a turn. A run of
// tool calls and thoughts folds into one row. Moved from today's Code tab
// (code/TranscriptItems.tsx there), without its cards, which are the one
// question card below the transcript (questions/QuestionCard.tsx), and
// without its check's row: checks run in the landing queue. A question once
// answered shows where its answer came in, in place of the prompt that
// carried it: what was asked, and what was answered.
//
// What a session writes is text: its Markdown goes through the board's
// sanitised renderer with raw HTML shown as text, and everything else (a
// command, an output, a diff, an input) is put on the page as text nodes.
import { createContext, memo, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { Me } from '../api/types'
import type { AskRecord } from '../questions/asks'
import { CheckIcon } from '../components/Icons'
import { clockTime, exactTime } from '../components/Time'
import { toast } from '../components/toasts'
import { Working } from '../components/Working'
import { Markdown } from '../markdown/Markdown'
import { senderName } from '../sessions/words'
import { cx, DiffView, FoldedText, FoldHead } from './bits'
import { COPIED_MS, copyText } from './copy'
import { countText, durationText, stopText } from './format'
import { AlertIcon, CopyIcon, DeniedIcon, StoppedIcon } from './icons'
import { ItemImages } from './ItemImages'
import { actionCounts, callCount, diffLines, diffStat, inputText, toolName, toolSummary, type Row } from './items'
import { planOfEntries, replyParts } from './plan'
import { PlanBlock } from './PlanBlock'
import { PromptText } from './PromptText'
import { SentFiles } from './SentFiles'
import type { AssistantItem, NoticeItem, PlanItem, ResultItem, ThinkingItem, ToolItem, UserItem } from './types'
import { sizeText } from './acp'

/** What the items of one transcript share: where its paths are, who reads it, whether it runs, and what opening a row tells it. */
export interface TranscriptActions {
  /** The session's folder: a path under it shows from there. */
  cwd: string | null
  /** Who reads the transcript: their own prompts are theirs. */
  me: Me | null
  /** The session runs: its last text may still be written. */
  busy: boolean
  /** He opened or closed something by hand: the transcript stops pulling to its end. */
  onFold: () => void
  /** Run a reply's shell block in the session's terminal; none where the page has no terminal. */
  onRun?: (command: string) => void
}

const NO_ACTIONS: TranscriptActions = { cwd: null, me: null, busy: false, onFold: () => {} }

export const TranscriptContext = createContext<TranscriptActions>(NO_ACTIONS)

const useActions = () => useContext(TranscriptContext)

/** What a prompt that carried an answer shows instead, when the page knows it (questions/asks.ts). */
export const AskRecords = createContext<((item: UserItem) => AskRecord | null) | null>(null)

// --- copying what was said ---------------------------------------------------------------

/**
 * Copy a reply or a prompt (SUN-1333): its text as the item holds it, a
 * reply's with its Markdown, goes to the clipboard, and the icon is a check
 * for a moment. `what` names it in the button's words: "reply", "prompt".
 */
function CopyButton({ text, what }: { text: string; what: string }) {
  const [copied, setCopied] = useState(false)
  const reset = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (reset.current !== null) clearTimeout(reset.current)
    },
    [],
  )
  const copy = () => {
    void copyText(text).then((done) => {
      if (!done) {
        toast(`Could not copy the ${what}.`, 'error')
        return
      }
      if (reset.current !== null) clearTimeout(reset.current)
      setCopied(true)
      reset.current = setTimeout(() => {
        reset.current = null
        setCopied(false)
      }, COPIED_MS)
    })
  }
  const label = copied ? 'Copied' : `Copy the ${what}`
  return (
    <button type="button" className={cx('code-copy', copied && 'is-copied')} aria-label={label} title={label} onClick={copy}>
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </button>
  )
}

// --- a prompt -----------------------------------------------------------------------------

/** Whether the person reading sent it. */
function isMine(item: UserItem, me: Me | null): boolean {
  return item.by.kind === 'person' && me?.member != null && item.by.member === me.member.id
}

/**
 * A prompt, set in from the replies. The reader's own carries no name;
 * another's says who sent it, and an answer to the session's question that
 * it was given on the board. An agent's words are not a person's: no accent
 * line (SUN-1335).
 */
/** What the server puts before a session's first prompt (crates/poulpe-server/src/board/sessions.rs), and after. */
const CONTEXT_START = 'From Poulpe, where this session stands:\n'
const CONTEXT_END = '\nThe prompt:\n'

/**
 * A prompt as the person wrote it, and the context Poulpe put before it for
 * the agent, when it carries one: the person's words show as today's, and
 * the context stays a fold away.
 */
export function promptParts(text: string): { context: string | null; prompt: string } {
  const end = text.startsWith(CONTEXT_START) ? text.indexOf(CONTEXT_END) : -1
  if (end < 0) return { context: null, prompt: text }
  return { context: text.slice(CONTEXT_START.length, end), prompt: text.slice(end + CONTEXT_END.length) }
}

function UserPrompt({ item }: { item: UserItem }) {
  const { onFold, me } = useActions()
  const records = useContext(AskRecords)
  const record = item.question ? (records?.(item) ?? null) : null
  if (record) return <AnsweredAsk item={item} record={record} />
  const { context, prompt } = item.opening ? promptParts(item.text) : { context: null, prompt: item.text }
  const mine = isMine(item, me)
  const who = senderName(item.by, me)
  const agent = item.by.kind === 'agent'
  // A prompt too large to keep whose sender was not kept either names no one.
  const unknown = item.omitted !== undefined && !item.omitted.known
  // Another lead session's message is its agent's words, never the person's (Poulpe-774).
  const message = item.message !== undefined && !mine && !unknown
  const via = item.via === 'board' ? 'On the board' : message ? `Message from ${who}` : mine || unknown ? null : `From ${who}`
  const label = mine ? 'Your prompt' : unknown ? 'A prompt' : message ? `Message from ${who}` : `Prompt from ${who}`
  return (
    <article className={cx('code-item code-user', agent && 'is-delegate')} aria-label={label} title={exactTime(item.at)}>
      {prompt.trim() !== '' && <CopyButton text={prompt} what="prompt" />}
      {via && <p className="code-user-via">{via}</p>}
      {context !== null && !item.omitted && (
        <details className="code-user-context" onToggle={onFold}>
          <summary>Poulpe's context</summary>
          <PromptText text={context} lines={14} onFold={onFold} />
        </details>
      )}
      {item.omitted ? (
        <p className="code-user-omitted">This prompt was too large to show{item.omitted.bytes === null ? '' : ` (${sizeText(item.omitted.bytes)})`}.</p>
      ) : (
        prompt.trim() !== '' && <PromptText text={prompt} lines={14} onFold={onFold} />
      )}
      <SentFiles files={item.files} />
    </article>
  )
}

/**
 * A question the session asked, once answered: who asked and whom, who
 * answered and when, the version answered when it was revised before, and
 * each prompt with its answer.
 */
function AnsweredAsk({ item, record }: { item: UserItem; record: AskRecord }) {
  const { me } = useActions()
  const member = me?.member?.id
  const byMe = record.by?.kind === 'person' && !!member && record.by.member === member
  const toMe = record.target ? record.target.kind === 'person' && record.target.member === member : byMe
  const who = byMe ? 'you' : (record.who ?? (record.by ? senderName(record.by, me) : null))
  const asked = `${record.asker} asked${toMe ? ' you' : ''}`
  return (
    <article className="code-item code-asked" aria-label={`${asked}, answered`} title={exactTime(item.at)}>
      <header className="code-asked-head">
        <CheckIcon size={13} />
        <strong>{asked}</strong>{' '}
        <span className="code-asked-state">
          {who ? `Answered by ${who}` : 'Answered'}
          {' · '}
          <time dateTime={record.at} title={exactTime(record.at)}>
            {clockTime(record.at, Date.now())}
          </time>
        </span>
        {record.version !== null && record.version > 1 && (
          <>
            {' '}
            <span className="code-asked-version">version {record.version}</span>
          </>
        )}
      </header>
      <dl className="code-answers code-asked-answers">
        {record.parts.map((part, i) => (
          <div key={i}>
            <dt>{part.text}</dt>
            <dd>{part.answer}</dd>
          </div>
        ))}
      </dl>
    </article>
  )
}

// --- the agent's text --------------------------------------------------------------------

/** A streaming text this long is drawn less often than it grows: rendering it costs more with every token. */
const SLOW_FROM = 8_000

/**
 * A text that streams, as it is drawn: every change while it is short, and
 * from `SLOW_FROM` characters on at an interval that grows with its length
 * (a fifth of a second at 20 KB, at most one second). Once it has stopped
 * streaming it is whole at once.
 */
export function useStreamedText(text: string, streaming: boolean): string {
  const slow = streaming && text.length >= SLOW_FROM
  const [drawn, setDrawn] = useState(text)
  const latest = useRef(text)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    latest.current = text
    if (!slow) {
      if (timer.current !== null) clearTimeout(timer.current)
      timer.current = null
      setDrawn(text)
      return
    }
    if (timer.current !== null) return
    timer.current = setTimeout(() => {
      timer.current = null
      setDrawn(latest.current)
    }, Math.min(1_000, text.length / 100))
  }, [text, slow])
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )
  return slow ? drawn : text
}

function AssistantText({ item, last }: { item: AssistantItem; last: boolean }) {
  const { busy, onRun } = useActions()
  // It is written while its turn is under way and nothing has followed it.
  const streaming = item.streaming && busy
  const text = useStreamedText(item.text, streaming)
  // A plan the agent printed is drawn as a checklist, where it stands in the reply (SUN-1346).
  const parts = useMemo(() => replyParts(text), [text])
  if (text.trim() === '' && !streaming) return null
  return (
    <article className={cx('code-item code-assistant', streaming && 'is-streaming')} aria-label="Reply" aria-busy={streaming || undefined}>
      {text.trim() !== '' &&
        parts.map((part, at) =>
          part.kind === 'plan' ? (
            <PlanBlock key={at} plan={part.plan} />
          ) : (
            // An agent's reply loads no image from another server.
            <Markdown key={at} source={part.text} className="code-md" localImagesOnly onRun={streaming ? undefined : onRun} />
          ),
        )}
      {streaming && <Working label="Writing" />}
      {/* Only the reply that ends a turn (SUN-1342), once it is written: the whole text, not what is drawn of it so far. */}
      {last && !streaming && (
        <div className="code-item-actions">
          <CopyButton text={item.text} what="reply" />
        </div>
      )}
    </article>
  )
}

// --- thinking ---------------------------------------------------------------------------

/** A thought's first line, as its folded row says it: a title Codex writes in bold, without its marks. */
function firstLine(text: string): string {
  const line = (text.trimStart().split('\n', 1)[0] ?? '').replace(/^\*\*(.+)\*\*$/, '$1')
  return line.length > 160 ? `${line.slice(0, 160)}…` : line
}

function Thinking({ item }: { item: ThinkingItem }) {
  const { onFold, busy } = useActions()
  const [open, setOpen] = useState(false)
  const id = useId()
  const streaming = item.streaming && busy
  const name = streaming ? 'Thinking' : 'Thought'
  if (item.text.trim() === '' && !streaming) return null
  return (
    <div className={cx('code-item code-fold code-thinking', streaming && 'is-streaming', open && 'is-open')}>
      <FoldHead
        open={open}
        controls={id}
        label={name}
        onToggle={() => {
          onFold()
          setOpen((was) => !was)
        }}
      >
        <span className="code-fold-name">{name}</span>
        {!open && <span className="code-fold-what">{firstLine(item.text)}</span>}
        {streaming && <Working label="Thinking" />}
      </FoldHead>
      {open && (
        <div id={id} className="code-fold-body">
          <FoldedText text={item.text} lines={40} className="code-thinking-text" onFold={onFold} />
        </div>
      )}
    </div>
  )
}

// --- a tool call ---------------------------------------------------------------------------

/** An edit this short shows its diff without being asked. */
const OPEN_DIFF_LINES = 24

const STATUS_NAME: Record<ToolItem['status'], string> = { running: 'Running', ok: 'Done', error: 'Failed', denied: 'Denied', stopped: 'Stopped' }

function ToolStatus({ status }: { status: ToolItem['status'] }) {
  const name = STATUS_NAME[status] ?? status
  return (
    <span className={`code-tool-status is-${status}`} role="img" aria-label={name} title={name}>
      {status === 'running' && <Working label={name} aria-hidden="true" />}
      {status === 'ok' && <CheckIcon size={12} />}
      {status === 'error' && <AlertIcon size={12} />}
      {status === 'denied' && <DeniedIcon size={12} />}
      {status === 'stopped' && <StoppedIcon size={12} />}
    </span>
  )
}

function ToolCall({ row, item }: { row: Row; item: ToolItem }) {
  const { cwd, onFold } = useActions()
  const id = useId()
  const { edits } = item
  const summary = useMemo(() => (edits.length > 1 ? `${edits.length} files` : toolSummary(item, cwd)), [item, edits, cwd])
  const stat = useMemo(() => (edits.length > 0 ? diffStat(edits.map((edit) => edit.diff).join('\n')) : null), [edits])
  const short = useMemo(
    () => edits.length > 0 && edits.reduce((count, edit) => count + diffLines(edit.diff).filter((line) => line.kind !== 'meta').length, 0) <= OPEN_DIFF_LINES,
    [edits],
  )
  // What he chose holds; until he does, a short edit is open and anything else closed.
  const [chosen, setChosen] = useState<boolean | null>(null)
  const open = chosen ?? short
  const calls = callCount(row)
  const name = toolName(item.name)
  // What follows a command's name is in its row's head already.
  const input = edits.length > 0 || item.command ? '' : inputText(item.input)
  return (
    <div className={cx('code-item code-fold code-tool', `is-${item.status}`, open && 'is-open')}>
      <FoldHead
        open={open}
        controls={id}
        label={summary ? `${name}: ${summary}` : name}
        onToggle={() => {
          onFold()
          setChosen(!open)
        }}
      >
        <ToolStatus status={item.status} />
        <span className="code-fold-name">{name}</span>
        <span className="code-fold-what code-tool-what" title={summary || undefined}>
          {summary}
        </span>
        {stat && (
          <span className="code-diff-stat">
            <span className="is-add">+{stat.additions}</span> <span className="is-del">−{stat.deletions}</span>
          </span>
        )}
        {calls > 0 && <span className="code-tool-count">{countText(calls, 'call')}</span>}
        {item.status === 'error' && <span className="code-tool-flag is-error">failed</span>}
        {item.status === 'denied' && <span className="code-tool-flag">denied</span>}
        {item.status === 'stopped' && <span className="code-tool-flag">stopped</span>}
      </FoldHead>
      {/* What its result shows is shown whether the row is open or not (SUN-1331). */}
      <ItemImages images={item.images} onFold={onFold} />
      {open && (
        <div id={id} className="code-fold-body">
          {edits.map((edit, i) => (
            <div key={`${edit.path}-${i}`}>
              {edits.length > 1 && <code>{edit.path}</code>}
              <DiffView diff={edit.diff} onFold={onFold} />
            </div>
          ))}
          {edits.length === 0 && item.command && <FoldedText text={item.command} lines={12} className="code-pre" onFold={onFold} />}
          {input !== '' && input !== '{}' && <FoldedText text={input} lines={20} className="code-pre" onFold={onFold} />}
          {row.children.length > 0 && (
            <div className="code-tool-calls" role="group" aria-label={`Calls of ${name}`}>
              {row.children.map((child) => (
                <ItemView key={child.item.id} row={child} />
              ))}
            </div>
          )}
          {item.output ? (
            <FoldedText text={item.output.text} lines={20} className={cx('code-pre code-output', item.status === 'error' && 'is-error')} onFold={onFold} />
          ) : (
            // An image it gave back is its output, shown under its row.
            edits.length === 0 &&
            !item.images?.length && <p className="code-quiet">{item.status === 'running' ? 'Running…' : item.status === 'denied' ? 'It was not run.' : item.status === 'stopped' ? 'It was stopped.' : 'No output.'}</p>
          )}
        </div>
      )}
    </div>
  )
}

// --- a run of actions --------------------------------------------------------------------------

const isLive = ({ item }: Row, busy: boolean) => (item.kind === 'tool' && item.status === 'running') || (item.kind === 'thinking' && item.streaming && busy)

/**
 * Tool calls and thoughts in a row, as one row that opens: closed, it says
 * how many of each, what failed, and while one still runs, which. Open, each
 * is the row it always is.
 */
export const ActionGroup = memo(
  function ActionGroup({ rows }: { rows: Row[] }) {
    const { cwd, onFold, busy } = useActions()
    const id = useId()
    const [open, setOpen] = useState(false)
    const tools = rows.flatMap(({ item }) => (item.kind === 'tool' ? [item] : []))
    // Closed, the row shows the images its calls hold; open, each call shows its own.
    const images = open ? [] : tools.flatMap((item) => item.images ?? [])
    const failed = tools.filter((item) => item.status === 'error').length
    const denied = tools.filter((item) => item.status === 'denied').length
    const live = [...rows].reverse().find((row) => isLive(row, busy))?.item
    const status: ToolItem['status'] = live ? 'running' : failed > 0 ? 'error' : 'ok'
    const name = countText(rows.length, 'action')
    const what = live ? (live.kind === 'tool' ? [toolName(live.name), toolSummary(live, cwd)].filter(Boolean).join(' · ') : 'Thinking') : actionCounts(rows).join(' · ')
    return (
      <div className={cx('code-item code-fold code-group', `is-${status}`, open && 'is-open')}>
        <FoldHead
          open={open}
          controls={id}
          label={name}
          onToggle={() => {
            onFold()
            setOpen((was) => !was)
          }}
        >
          <ToolStatus status={status} />
          <span className="code-fold-name">{name}</span>
          <span className={cx('code-fold-what', live && 'code-tool-what')} title={what || undefined}>
            {what}
          </span>
          {failed > 0 && <span className="code-tool-flag is-error">{failed} failed</span>}
          {denied > 0 && <span className="code-tool-flag">{denied} denied</span>}
        </FoldHead>
        <ItemImages images={images} onFold={onFold} />
        {open && (
          <div id={id} className="code-fold-body code-group-body" role="group" aria-label={name}>
            {rows.map((row) => (
              <ItemView key={row.item.id} row={row} />
            ))}
          </div>
        )}
      </div>
    )
  },
  // Its rows are the objects they were until one changes: then, and only then, it draws again.
  (before, after) => before.rows.length === after.rows.length && before.rows.every((row, i) => row === after.rows[i]),
)

// --- the turn's plan, a notice, and the end of a turn -----------------------------------------------

function TurnPlan({ item }: { item: PlanItem }) {
  const plan = useMemo(() => planOfEntries(item.entries), [item.entries])
  return (
    <div className="code-item code-turn-plan">
      <PlanBlock plan={plan} />
    </div>
  )
}

function Notice({ item }: { item: NoticeItem }) {
  return (
    <p className={cx('code-item code-notice', `is-${item.level}`)} role="note" title={exactTime(item.at)}>
      {item.level !== 'info' && <AlertIcon size={13} />}
      <span>{item.text}</span>
    </p>
  )
}

/** The line that ends a turn: how long it took, when its prompt is known, and why it stopped when it did not simply end. */
function TurnFooter({ item }: { item: ResultItem }) {
  const stop = stopText(item.stop_reason)
  return (
    <p className={cx('code-item code-result', stop && 'is-stopped')} title={exactTime(item.at)}>
      {item.duration_ms !== null && <span>{durationText(item.duration_ms)}</span>}
      {stop && <span className="code-result-stop">{stop}</span>}
    </p>
  )
}

// --- one item --------------------------------------------------------------------------------------------

/**
 * One row of the transcript. It draws again only when its item, or a call
 * under it, changed. `last` says a reply is the one that ends its turn
 * (`lastReplies`): the one with the copy icon.
 */
export const ItemView = memo(function ItemView({ row, last = false }: { row: Row; last?: boolean }) {
  const { item } = row
  switch (item.kind) {
    case 'user':
      return <UserPrompt item={item} />
    case 'assistant':
      return <AssistantText item={item} last={last} />
    case 'thinking':
      return <Thinking item={item} />
    case 'tool':
      return <ToolCall row={row} item={item} />
    case 'plan':
      return <TurnPlan item={item} />
    case 'notice':
      return <Notice item={item} />
    case 'result':
      return <TurnFooter item={item} />
    default:
      return null
  }
})
